<template>
    <div>
        <!-- Phase 1: Want to create an account? -->
        <div v-if="phase === 'ask'">
            <h3 class="my-4 text-center h4">{{ getTranslatedText('__create_comparehub_account__', 'steps.titles') }}</h3>
            <div class="mb-3" v-html="getTranslatedText('__create_comparehub_account_benefits__', 'steps.textTop')"></div>
            <div class="mb-3">
                <RadioButton
                    name="wants_to_create_account"
                    :choices="registrationChoices"
                    @inputFromRadioButton="onRegistrationAnswer"
                />
            </div>
            <div class="mt-2" v-html="getTranslatedText('__create_comparehub_account_opt_in__', 'steps.textBottom')"></div>
        </div>

        <!-- Phase 2a: SMS verification -->
        <div v-if="phase === 'sms'" class="pb-3">
            <h3 class="my-4 text-center h4">{{ getTranslatedText('__phone_verification__', 'steps.titles') }}</h3>
            <SmsVerification
                name="sms_verification"
                :hideSubtitles="false"
                @goToNextStep="onSmsCompleted"
            />
        </div>

        <!-- Phase 2b: Email/password form -->
        <div v-if="phase === 'email'">
            <h3 class="my-4 text-center h4">{{ getTranslatedText('__lets_create_your_account__', 'steps.titles') }}</h3>
            <div class="row mb-3" v-for="field in emailFields" :key="field.name">
                <div class="col-md-8 offset-md-2">
                    <label :for="field.name">{{ getTranslatedText(field.label, 'fields.labels') }}</label>
                    <input
                        class="form-control"
                        :type="field.type"
                        :name="field.name"
                        :id="field.name"
                        :maxlength="field.maxlength"
                        :disabled="field.disabled"
                        :value="app[field.name]"
                        v-bind="field.noPaste ? { onpaste: 'return false;' } : {}"
                        @input="onFieldInput(field.name, $event.target.value)"
                        @blur="onFieldBlur(field.name, $event.target.value)"
                    />
                    <LcFieldErrors
                        v-if="(shouldValidate || validators[field.name]?.isDirty) && !validators[field.name]?.isValid"
                        :errors="validators[field.name]?.errorMessage"
                    />
                </div>
            </div>
            <div class="mb-3 mt-2" v-html="getTranslatedText('__comparehub_member_pp_tc__', 'steps.textBottom')"></div>
            <div class="row mt-3">
                <div class="col-6 col-md-4" style="text-align:center;">
                    <a class="btn btn-lg w-100"
                        :class="'btn-' + (applicationSettings.formStyle?.buttonColor?.skip ?? 'light')"
                        @click.prevent="onSkip"
                    >{{ getTranslatedText('__skip__', 'buttons') }}</a>
                </div>
                <div class="col-6 col-md-4" style="text-align:center;">
                    <a class="btn btn-lg w-100"
                        :class="'btn-' + (applicationSettings.formStyle?.buttonColor?.next ?? 'primary')"
                        @click.prevent="onSubmitEmail"
                    >{{ getTranslatedText('__next__', 'buttons') }}</a>
                </div>
            </div>
        </div>

        <!-- Phase 3: Want EID verification? -->
        <div v-if="phase === 'ask-verify'">
            <h3 class="my-4 text-center h4">{{ getTranslatedText('__wants_equifax_verification__', 'steps.titles') }}</h3>
            <div class="mb-3" v-html="getTranslatedText('__prequalify_best_providers__', 'steps.textTop')"></div>
            <RadioButton
                name="wants_verification"
                :choices="verificationChoices"
                @inputFromRadioButton="onVerificationAnswer"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, inject, onBeforeUnmount } from 'vue';
import debounce from 'lodash.debounce';
import RadioButton from '@/components/form/inputs/RadioButton.vue';
import SmsVerification from '@/components/form/inputs/SmsVerification.vue';
import LcFieldErrors from '@/components/form/LcFieldErrors.vue';
import { fieldValidationHelper } from '@/form/helpers/field-validations.js';
import { useInjectedApplicationSettings } from '@/composables/useApplicationSettingsProvider.js';
import { useFormSubmission } from '@/composables/useFormSubmission.js';
import { scrollToTop } from '@/utils/scroll.js';
import { clearNotifications } from '@/utils/notifications.js';

defineProps({
    name: { type: String, required: true },
});
const emit = defineEmits(['completed']);

const { app, updateApplicationField, persistLocally, updateApplication, processedApplication, getTranslatedText } = inject('app');
const applicationSettings = useInjectedApplicationSettings();

const _submission = useFormSubmission({
    app,
    applicationSettings,
    updateApplication,
    processedApplication,
    isSubmitting: ref(false),
    loading: { startRotatingMessages() {}, stopRotatingMessages() {} },
});

// --- Phase machine ---
const phase = ref('ask');
const shouldValidate = ref(false);

// --- Choices ---
const registrationChoices = [
    { text: '__opt_yes__', value: 'yes' },
    { text: '__opt_not_right_now__', value: 'no' },
];

const verificationChoices = [
    { text: '__opt_yes__', value: 'yes' },
    { text: '__opt_not_right_now__', value: 'no' },
];

// --- Email/password field definitions ---
const emailFields = [
    { name: 'email', label: '__email_address__', type: 'text', rules: 'required|email', maxlength: 50, disabled: true },
    { name: 'email_confirmation', label: '__confirm_email__', type: 'text', rules: 'required|email|emailConfirmation', maxlength: 50, noPaste: true },
    { name: 'password', label: '__password__', type: 'password', rules: 'required|password', maxlength: 50 },
    { name: 'password_confirmation', label: '__confirm_password__', type: 'password', rules: 'required|password|passwordConfirmation', maxlength: 50 },
];

// --- Validators ---
const validators = reactive({});
function initValidators() {
    for (const field of emailFields) {
        validators[field.name] = {
            value: app.value[field.name] ?? null,
            isValid: false,
            isDirty: false,
            errorMessage: [],
            rules: field.rules,
        };
    }
}
initValidators();

const debouncedValidate = debounce((fieldName) => {
    validators[fieldName].isDirty = true;
    const result = fieldValidationHelper(validators[fieldName], app.value);
    validators[fieldName].isValid = result.isValid;
    validators[fieldName].errorMessage = result.messages;
}, 500);

onBeforeUnmount(() => {
    debouncedValidate.cancel();
});

function onFieldInput(fieldName, value) {
    const fieldDef = emailFields.find(f => f.name === fieldName);
    if (fieldDef?.disabled) {
        const el = document.getElementById(fieldName);
        if (el) el.value = app.value[fieldName] ?? '';
        return;
    }
    updateApplicationField(fieldName, value);
    validators[fieldName].value = value;
    debouncedValidate(fieldName);
}

function onFieldBlur(fieldName, value) {
    const fieldDef = emailFields.find(f => f.name === fieldName);
    if (fieldDef?.disabled) return;

    updateApplicationField(fieldName, value);
    validators[fieldName].value = value;
    validators[fieldName].isDirty = true;

    const result = fieldValidationHelper(validators[fieldName], app.value);
    validators[fieldName].isValid = result.isValid;
    validators[fieldName].errorMessage = result.messages;
}

function validateAllFields() {
    let allValid = true;
    for (const field of emailFields) {
        validators[field.name].value = app.value[field.name] ?? null;
        const result = fieldValidationHelper(validators[field.name], app.value);
        validators[field.name].isValid = result.isValid;
        validators[field.name].errorMessage = result.messages;
        if (!result.isValid) {
            allValid = false;
        }
    }
    return allValid;
}

// --- Phase 1: Registration question ---
function onRegistrationAnswer(value) {
    updateApplicationField('wants_to_create_account', value);
    if (value === 'yes') {
        applicationSettings.userWantsRegistration = true;
        enterRegistrationPhase();
    } else {
        applicationSettings.userWantsRegistration = false;
        applicationSettings.isUserAuthenticated = false;
        applicationSettings.userSkippedAuthentication = true;
        emit('completed');
    }
}

function enterRegistrationPhase() {
    scrollToTop();
    const phoneAlreadyVerified = app.value.verified_phone_number?.length === 10;
    phase.value = phoneAlreadyVerified ? 'email' : 'sms';
}

// --- Phase 2a: SMS completed ---
function onSmsCompleted() {
    scrollToTop();
    const shouldSkipEmail = app.value.forcePhoneVerification &&
        (!app.value.verified_phone_number || app.value.verified_phone_number.length !== 10);

    if (shouldSkipEmail) {
        applicationSettings.isUserAuthenticated = false;
        applicationSettings.userWantsVerification = false;
        emit('completed');
    } else {
        phase.value = 'email';
    }
}

// --- Phase 2b: Email/password submit ---
async function onSubmitEmail() {
    shouldValidate.value = true;
    if (!validateAllFields()) {
        return;
    }
    persistLocally(); // [DISABLED] currently a no-op — see useApplicationCore.js
    const success = await _submission.executeCreateAccount();
    if (!success) {
        applicationSettings.notifications.error = {
            account_creation: 'We were unable to create your account at this time. You can continue your application without an account.',
            _autoDismissSeconds: 5,
        };
        applicationSettings.userWantsRegistration = false;
        applicationSettings.userWantsVerification = false;
        applicationSettings.isUserAuthenticated = false;
        applicationSettings.userSkippedAuthentication = true;
        emit('completed');
        return;
    }
    goToVerificationOrFinish();
}

function onSkip() {
    clearNotifications(applicationSettings);
    applicationSettings.userWantsRegistration = false;
    applicationSettings.userWantsVerification = false;
    applicationSettings.isUserAuthenticated = false;
    applicationSettings.userSkippedAuthentication = true;
    emit('completed');
}

// --- Phase 3: Verification question ---
function onVerificationAnswer(value) {
    updateApplicationField('wants_verification', value);
    applicationSettings.userWantsVerification = value === 'yes';
    emit('completed');
}

// --- Navigation helper ---
function goToVerificationOrFinish() {
    const shouldSkip = (!!app.value.forcePhoneVerification && !app.value.isPhoneVerified)
        || applicationSettings.userSkippedAuthentication;

    if (shouldSkip) {
        applicationSettings.userWantsVerification = false;
        applicationSettings.isUserVerified = false;
        emit('completed');
    } else {
        phase.value = 'ask-verify';
    }
}
</script>
